import type { ReactElement } from 'react';
import { Text } from '@mantine/core';
import SurfaceActivityTabsBody from '@/shared/surfaces/SurfaceActivityTabsBody';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { ActivityOrder } from '@/shared/surfaces/SurfaceActivityViewModel';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { dimValue } from '@/shared/surfaces/InteractionRecordDrawer';
import { formatCount, formatTimestamp, humanUser } from '@/shared/analytics/MetricFormatting';

const HEAVIEST_FIRST: ActivityOrder = { column: 'outputTokens', direction: 'desc' };

/** The cost cut of the activity feed: consumption columns instead of the classification ones. */
const TOKEN_USAGE_ACTIVITY_COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'person',
        header: 'Person',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => <Text size="xs">{dimValue(r, 'user_department')}</Text>,
    },
    {
        key: 'model',
        header: 'Model',
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'category',
        header: 'Category',
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'in',
        header: 'In',
        align: 'right',
        sortValue: (r) => r.inputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatCount(r.inputTokens)}
            </Text>
        ),
    },
    {
        key: 'out',
        header: 'Out',
        align: 'right',
        sortValue: (r) => r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatCount(r.outputTokens)}
            </Text>
        ),
    },
    {
        key: 'total',
        header: 'Total',
        align: 'right',
        sortValue: (r) => r.inputTokens + r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatCount(r.inputTokens + r.outputTokens)}
            </Text>
        ),
    },
];

/** The Token Usage landing: the shared Activity/Analytics tabbed body, cut for consumption — heaviest prompts first with token columns. */
export default function TokenUsageRegisterBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    return (
        <SurfaceActivityTabsBody
            surface={surface}
            accent="teal"
            activityIntro="Where consumption actually goes, heaviest prompts first — the people, departments, and models behind the token volume. Open a row to read the conversation."
            persistenceKey="spend-activity"
            columns={TOKEN_USAGE_ACTIVITY_COLUMNS}
            orderBy={HEAVIEST_FIRST}
            truncationDescriptor="heaviest"
        />
    );
}
